import { NavLink, Outlet } from "react-router-dom";

import { useAuth } from "@/hooks/useAuth";
import { usePendingApprovals } from "@/hooks/useApprovals";
import { useMyProfile } from "@/hooks/useMe";
import { useSystemHealth } from "@/hooks/useSystemHealth";
import { cn } from "@/lib/utils";

/**
 * Hub "Operación de respuesta automática": layout que concentra las vistas de
 * respuesta automática del NOC/SOC bajo un menú horizontal (Incidentes, Nuevo
 * análisis, Aprobaciones, Historial de Autorremediaciones, Catálogo de SRPs).
 *
 * Es un layout route SIN path (App.tsx): no cambia las rutas de las vistas, solo
 * comparte el menú. Cada pestaña respeta el gating individual que tenía en el
 * menú lateral (mismo criterio `has()`), y Aprobaciones
 * conserva el badge de pendientes. El título de cada vista lo da su propia
 * página (su `<h1>`); aquí solo va un rótulo discreto sobre las pestañas.
 */
interface HubTab {
  to: string;
  label: string;
  end: boolean;
  show: boolean;
  badge: number | null;
}

export function OperacionRespuestaPage() {
  const { user } = useAuth();
  const profileQuery = useMyProfile();
  const segments = new Set(profileQuery.data?.segments ?? []);
  const isSuper =
    (profileQuery.data?.global_roles ?? []).includes("super_user") || user?.role === "admin";
  // Mismo criterio que el menú lateral: mientras carga el perfil, mostrar todo.
  const has = (code: string) => profileQuery.isPending || isSuper || segments.has(code);

  const healthQuery = useSystemHealth();
  const analysisEnabled = healthQuery.data?.analysis_enabled ?? false;
  // Compuerta propia: Aprobaciones vive aunque no haya modulo de analisis,
  // porque la propuesta que se aprueba la genera el match del SOAR, no el
  // modelo. Atarla a `analysis_enabled` escondia la pestana sin motivo.
  const approvalsEnabled = healthQuery.data?.approvals_enabled ?? false;
  const approvalsQuery = usePendingApprovals();
  const pendingCount = approvalsQuery.data?.length ?? 0;

  const tabs: HubTab[] = [
    { to: "/", label: "Incidentes", end: true, show: has("dashboard"), badge: null },
    {
      to: "/analysis/new",
      label: "Nuevo análisis",
      end: false,
      show: has("analysis") && analysisEnabled,
      badge: null,
    },
    {
      to: "/approvals",
      label: "Aprobaciones",
      end: false,
      show: has("approvals") && approvalsEnabled,
      badge: approvalsEnabled && pendingCount > 0 ? pendingCount : null,
    },
    {
      to: "/runs",
      label: "Historial de Autorremediaciones",
      end: false,
      show: has("runs"),
      badge: null,
    },
    { to: "/srp/catalog", label: "Catálogo de SRPs", end: false, show: has("srp"), badge: null },
  ];
  const visible = tabs.filter((t) => t.show);

  return (
    <div className="flex h-full flex-col gap-4">
      <div className="space-y-2">
        <div className="text-[10px] font-medium uppercase tracking-[0.16em] text-muted-foreground">
          Operación de respuesta automática
        </div>
        <div className="flex gap-1 overflow-x-auto border-b">
          {visible.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              className={({ isActive }) =>
                cn(
                  "flex items-center gap-2 whitespace-nowrap rounded-t-md border-b-2 px-4 py-2 text-sm font-medium transition-colors",
                  isActive
                    ? "border-primary text-foreground"
                    : "border-transparent text-muted-foreground hover:text-foreground",
                )
              }
            >
              {item.label}
              {item.badge != null && (
                <span className="rounded-full bg-amber-500 px-2 py-0.5 text-[10px] font-semibold text-white">
                  {item.badge}
                </span>
              )}
            </NavLink>
          ))}
        </div>
      </div>
      <div className="flex-1">
        <Outlet />
      </div>
    </div>
  );
}
